import React, { useMemo } from 'react'
import {
    Box,
    Card,
    CardContent,
    Container,
    Stack,
    Typography,
} from '@mui/material'
import { useLanguage } from '../../contexts/LanguageContext'
import { useAuth } from '../../contexts/AuthContext'
import { getMerchantTaxRemittances } from '../../utils/merchantToolStorage'

const panelStyles = {
    borderRadius: 6,
    background: 'rgba(27,16,64,0.96)',
    border: '1px solid rgba(183,166,255,0.16)',
}

export default function GovernmentNotifications() {
    const { lang } = useLanguage()
    const { user } = useAuth()
    const isFr = lang === 'fr'
    const authority = user?.authority || ''
    const agencyName =
        user?.agencyName ||
        user?.name ||
        (isFr ? 'Agence fiscale' : 'Tax Authority')

    const allReceipts = useMemo(() => getMerchantTaxRemittances(), [])
    const receipts = useMemo(() => {
        if (!authority) return allReceipts
        return allReceipts.filter((item) => {
            const itemAuthority = item.authority || item.taxType || ''
            return itemAuthority === authority
        })
    }, [allReceipts, authority])

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
            <Stack spacing={3}>
                <Card
                    sx={{
                        borderRadius: 6,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(42,27,102,1) 0%, rgba(91,46,255,1) 100%)',
                        boxShadow: '0 28px 60px rgba(49,26,137,0.38)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={1.5}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.74)' }}>
                                {isFr ? 'Notifications de l autorite' : 'Authority notifications'}
                            </Typography>
                            <Typography
                                sx={{
                                    color: '#FFFFFF',
                                    fontWeight: 900,
                                    fontSize: { xs: 30, md: 42 },
                                    letterSpacing: '-0.03em',
                                }}
                            >
                                {isFr ? 'Alertes et recus' : 'Alerts & Receipts'}
                            </Typography>
                            <Typography sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: 20 }}>
                                {agencyName}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.80)', maxWidth: 860 }}>
                                {isFr
                                    ? 'Ne voyez que les recus, references et activites lies a votre autorite fiscale.'
                                    : 'Only see the receipts, references, and activity tied to your tax authority.'}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={panelStyles}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                            {isFr ? 'Notifications recentes' : 'Recent notifications'}
                        </Typography>
                        {receipts.length === 0 ? (
                            <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                {isFr
                                    ? 'Aucune notification fiscale pour cette autorite pour le moment.'
                                    : 'No tax notifications for this authority yet.'}
                            </Typography>
                        ) : (
                            <Stack spacing={1.2}>
                                {receipts.slice(0, 20).map((item) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            p: 1.8,
                                            borderRadius: 4,
                                            bgcolor: 'rgba(91,46,255,0.10)',
                                            border: '1px solid rgba(183,166,255,0.16)',
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 800 }}>
                                            {item.receiptNumber}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.5 }}>
                                            {(item.authority || authority)} • {item.country}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.5 }}>
                                            {(item.reference || '').trim() || (isFr ? 'Sans reference' : 'No reference')}
                                        </Typography>
                                        <Typography sx={{ color: '#FF8A00', fontWeight: 800, mt: 0.75 }}>
                                            {Number(item.amount || 0).toFixed(2)}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 0.5 }}>
                                            {new Date(item.createdAt).toLocaleString()}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Stack>
        </Container>
    )
}
